import React, { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Connexion() {
  const { user, login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", company: "", phone: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || null;

  if (user && user !== false) {
    return <Navigate to={redirectTo || (user.role === "admin" ? "/admin" : "/espace-client")} replace />;
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (mode === "login") {
        const u = await login(form.email, form.password);
        nav(redirectTo || (u.role === "admin" ? "/admin" : "/espace-client"));
      } else {
        await register({
          email: form.email,
          password: form.password,
          name: form.name,
          company: form.company,
          phone: form.phone,
        });
        nav("/espace-client");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white pt-32 pb-16 flex items-start" data-testid="login-page">
      <div className="max-w-6xl w-full mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5 hidden md:block">
          <div className="hazard-stripes h-3 w-24 mb-6" />
          <h1 className="font-display font-bold uppercase tracking-tighter text-5xl leading-[0.92]">
            Espace<br /><span className="text-[#ffd600]">client.</span>
          </h1>
          <p className="text-neutral-400 mt-6 leading-relaxed">
            Suivez l’avancement de votre chantier, consultez vos documents et échangez avec votre conducteur de travaux — où que vous soyez.
          </p>
          <ul className="mt-8 space-y-3 text-neutral-300">
            <li className="flex items-start gap-3"><i className="fa-solid fa-chart-line text-[#ffd600] mt-1" /> Avancement en temps réel</li>
            <li className="flex items-start gap-3"><i className="fa-solid fa-images text-[#ffd600] mt-1" /> Photos de chantier</li>
            <li className="flex items-start gap-3"><i className="fa-solid fa-file-lines text-[#ffd600] mt-1" /> Documents partagés</li>
            <li className="flex items-start gap-3"><i className="fa-solid fa-comments text-[#ffd600] mt-1" /> Messagerie chantier</li>
          </ul>

          <div className="brut-card !p-5 mt-10">
            <div className="font-mono-tech text-xs uppercase tracking-widest text-[#ffd600]">/ Compte démo</div>
            <div className="text-neutral-300 mt-2 text-sm">
              <div><strong className="font-display uppercase">Client :</strong> client@dcr-bati.fr / ClientDemo2026!</div>
              <div className="mt-1"><strong className="font-display uppercase">Admin :</strong> admin@dcr-bati.fr / DcrAdmin2026!</div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="md:col-span-7 brut-card !p-8 md:!p-10" data-testid="login-form">
          <div className="flex gap-2 mb-8">
            <button
              type="button"
              onClick={() => setMode("login")}
              data-testid="tab-login"
              className={`font-display uppercase tracking-widest text-sm py-3 px-5 border ${mode === "login" ? "bg-[#ffd600] text-black border-[#ffd600]" : "text-neutral-300 border-white/10"}`}
            >
              Connexion
            </button>
            <button
              type="button"
              onClick={() => setMode("register")}
              data-testid="tab-register"
              className={`font-display uppercase tracking-widest text-sm py-3 px-5 border ${mode === "register" ? "bg-[#ffd600] text-black border-[#ffd600]" : "text-neutral-300 border-white/10"}`}
            >
              Créer un compte
            </button>
          </div>

          {mode === "register" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
              <div>
                <label className="label-field">Nom complet *</label>
                <input required value={form.name} onChange={set("name")} className="input-field" data-testid="login-name" />
              </div>
              <div>
                <label className="label-field">Société</label>
                <input value={form.company} onChange={set("company")} className="input-field" data-testid="login-company" />
              </div>
              <div className="md:col-span-2">
                <label className="label-field">Téléphone</label>
                <input value={form.phone} onChange={set("phone")} className="input-field" data-testid="login-phone" />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-5">
            <div>
              <label className="label-field">Email *</label>
              <input required type="email" value={form.email} onChange={set("email")} className="input-field" data-testid="login-email" />
            </div>
            <div>
              <label className="label-field">Mot de passe *</label>
              <input required type="password" value={form.password} onChange={set("password")} className="input-field" data-testid="login-password" />
            </div>
          </div>

          {error && (
            <div className="mt-5 p-4 border border-red-500/40 text-red-300 bg-red-500/10 font-mono-tech text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <button type="submit" disabled={loading} className="btn-primary mt-7 w-full justify-center" data-testid="login-submit">
            {loading ? "Connexion…" : mode === "login" ? "Se connecter" : "Créer mon compte"} <i className="fa-solid fa-arrow-right" />
          </button>

          <div className="mt-6 text-center font-mono-tech text-xs uppercase tracking-widest text-neutral-500">
            <Link to="/" className="hover:text-[#ffd600]">← Retour au site</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
